import HttpService from './HttpService';

const PAINTS_URL = '/paints';
const ROOMS_URL = '/rooms';

/**
 * Format error for consistent handling across components.
 * Matches the pattern used by RoomService — callers wrap in try/catch and
 * read .message / .code / .details from the thrown object.
 * @param {Error} error - Axios error
 * @param {string} defaultMessage - Human-readable fallback
 * @returns {Object} - { message, code, originalError, details }
 */
const formatError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.message || defaultMessage;
  const errorCode = error.response?.status;
  const details = error.response?.data?.details || {};

  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }

  return { message: errorMessage, code: errorCode, originalError: error, details };
};

const PaintService = {
  // ==================== SHARED PAINT LIBRARY ====================

  /**
   * List paints in the current user's library, paginated.
   * @param {Object} [params]
   * @param {number} [params.limit=50] - Max paints per page (server caps at 100)
   * @param {number} [params.offset=0] - Pagination offset
   * @returns {Promise<{paints: Array, pagination: {total, limit, offset, has_more}}>}
   */
  listPaints: async (params = {}) => {
    try {
      const response = await HttpService.get(PAINTS_URL, { params });
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching paints');
    }
  },

  /**
   * Get a single paint by ID.
   * @param {number} paintId
   * @returns {Promise<Object>} - Paint
   */
  getPaint: async (paintId) => {
    try {
      const response = await HttpService.get(`${PAINTS_URL}/${paintId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching paint');
    }
  },

  /**
   * Create a paint in the user's library.
   * Requires at least one of color_name or color_code (backend CHECK constraint).
   * @param {Object} data - PaintCreate-shaped body
   * @returns {Promise<Object>} - Created paint
   */
  createPaint: async (data) => {
    try {
      const response = await HttpService.post(PAINTS_URL, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating paint');
    }
  },

  /**
   * Partial update of a paint. Only provided fields are touched.
   * Editing affects every room that references this paint via RoomPaint.
   * @param {number} paintId
   * @param {Object} data - PaintUpdate-shaped body (any subset)
   * @returns {Promise<Object>} - Updated paint
   */
  updatePaint: async (paintId, data) => {
    try {
      const response = await HttpService.put(`${PAINTS_URL}/${paintId}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error updating paint');
    }
  },

  /**
   * Delete a paint. Cascades all RoomPaint references via FK.
   * R2 image cleanup happens server-side after the DB commit succeeds.
   * @param {number} paintId
   * @returns {Promise<Object>} - {success, message}
   */
  deletePaint: async (paintId) => {
    try {
      const response = await HttpService.delete(`${PAINTS_URL}/${paintId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error deleting paint');
    }
  },

  /**
   * Upload one image to a paint's gallery (up to 5 per paint, enforced server-side).
   * First image is auto-promoted to primary. Mirrors RoomService.uploadRoomImage.
   * @param {number} paintId
   * @param {File} file
   * @param {boolean} [isPrimary=false] - When true, promote this image to primary
   *   on upload (any existing primary is demoted in the same transaction).
   * @returns {Promise<Object>} - PaintImageSchema row {id, url, is_primary, ...}
   */
  uploadPaintImage: async (paintId, file, isPrimary = false) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (isPrimary) formData.append('is_primary', 'true');
      const response = await HttpService.post(`${PAINTS_URL}/${paintId}/images`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error uploading paint image');
    }
  },

  /**
   * Delete one image from a paint's gallery. If the deleted image was primary,
   * the next-oldest image is auto-promoted server-side.
   * @param {number} paintId
   * @param {number} imageId
   * @returns {Promise<Object>} - {success, message}
   */
  deletePaintImage: async (paintId, imageId) => {
    try {
      const response = await HttpService.delete(`${PAINTS_URL}/${paintId}/images/${imageId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error deleting paint image');
    }
  },

  /**
   * Promote a specific image to primary; the previous primary is demoted in the
   * same transaction (see api/paints.py with_for_update lock).
   * @param {number} paintId
   * @param {number} imageId
   * @returns {Promise<Object>} - Updated PaintImageSchema row
   */
  setPaintImagePrimary: async (paintId, imageId) => {
    try {
      const response = await HttpService.put(`${PAINTS_URL}/${paintId}/images/${imageId}/primary`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error setting primary paint image');
    }
  },

  /**
   * Scan a paint label photo and return a draft PaintCreate payload.
   * Does NOT persist a Paint — caller reviews the draft and POSTs to createPaint
   * separately. Single file only (backend rejects batch to avoid Gemini
   * cross-label hallucination — see docs/maintenance/paint_scan_benchmark.md).
   *
   * Returns 200 even on extraction failure; check response.success.
   * @param {File} file - Label photo (jpg/jpeg/png/gif/webp, max 10MB)
   * @returns {Promise<{success: boolean, paint: Object|null, confidence: number|null, error: string|null, error_code: string|null}>}
   */
  scanPaintLabel: async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await HttpService.post(`${PAINTS_URL}/scan`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error scanning paint label');
    }
  },

  // ==================== ROOM↔PAINT JUNCTION ====================

  /**
   * List paints applied to a room, with embedded Paint details on each row.
   * @param {number} roomId
   * @returns {Promise<Array>} - RoomPaint rows, each with `.paint` embedded
   */
  getRoomPaints: async (roomId) => {
    try {
      const response = await HttpService.get(`${ROOMS_URL}/${roomId}/paints`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching room paints');
    }
  },

  /**
   * Attach an existing paint from the user's library to a room.
   * Cross-user paint_id returns 404 (not 403) — the API doesn't leak the
   * existence of other users' paints.
   * @param {number} roomId
   * @param {Object} data - {paint_id, surface, date_painted?, notes?}
   * @returns {Promise<Object>} - Created RoomPaint with embedded paint
   */
  attachPaintToRoom: async (roomId, data) => {
    try {
      const response = await HttpService.post(`${ROOMS_URL}/${roomId}/paints`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error attaching paint to room');
    }
  },

  /**
   * Update junction metadata (surface, date_painted, notes).
   * Does NOT change the underlying Paint — use updatePaint for that.
   * @param {number} roomId
   * @param {number} roomPaintId
   * @param {Object} data - RoomPaintUpdate-shaped body
   * @returns {Promise<Object>} - Updated RoomPaint with embedded paint
   */
  updateRoomPaint: async (roomId, roomPaintId, data) => {
    try {
      const response = await HttpService.put(
        `${ROOMS_URL}/${roomId}/paints/${roomPaintId}`,
        data
      );
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error updating room paint');
    }
  },

  /**
   * Detach a paint from a room. The underlying Paint persists in the library.
   * To delete the Paint entirely, call deletePaint(paintId) after detaching.
   * @param {number} roomId
   * @param {number} roomPaintId
   * @returns {Promise<Object>} - {success, message}
   */
  detachPaintFromRoom: async (roomId, roomPaintId) => {
    try {
      const response = await HttpService.delete(
        `${ROOMS_URL}/${roomId}/paints/${roomPaintId}`
      );
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error detaching paint from room');
    }
  },
};

export default PaintService;
